<template>
  <div class="modal-mask">
    <div class="modal-content-container">
      <div class="user-management__header-container">
        <span class="user-management__header-title">Gerenciar Perfis</span>
        <span @click="closeDialog" class="material-icons closebtn-icon">cancel</span>
      </div>

      <div class="permission-modal">
        <cc-loader v-if="loading" class="permission-modal__main-loader" />

        <template v-else>
          <section class="permission-modal__panel">
            <div class="input-title">Perfis</div>
            <div class="input-container">
              <input
                type="text"
                v-model="new_profile.name"
                class="input-value"
                placeholder="Adicione um novo perfil"
                @keyup.enter="new_profile.name && save(new_profile)"
              />
              <span
                class="material-icons input-icon"
                @click="new_profile.name && save(new_profile)"
              >
                add_circle_outline
              </span>
            </div>

            <div class="profiles-wrapper">
              <div class="page-table-container">
                <div class="page-table-content">
                  <div class="page-table-header">
                    <div class="page-table-header-text permission-modal__id-column">#ID</div>
                    <div class="page-table-header-text permission-modal__name-column">Nome</div>
                    <div class="page-table-header-text permission-modal__actions-column">Ações</div>
                  </div>

                  <div
                    v-for="profile in profiles"
                    :key="'prof-' + profile.id"
                    :class="{
                      selected: isSelectedProfile(profile),
                      'permission-modal__readonly-row': profile.is_base,
                    }"
                    @click.stop="select_profile(profile)"
                  >
                    <div class="page-table-row permission-modal__profile-row">
                      <div class="page-table-desc-column permission-modal__id-column">
                        <span class="permission-modal__id-trigger">
                          <span class="material-icons-outlined permission-modal__id-icon">touch_app</span>
                          <b>#{{ profile.id }}</b>
                        </span>
                      </div>

                      <div class="page-table-desc-column permission-modal__name-column">
                        <div class="permission-modal__profile-name">
                          <cc-input
                            v-model="profile.name"
                            :disabled="!isSelectedProfile(profile) || profile.is_base"
                          />
                          <small v-if="profile.is_base" class="permission-modal__badge">Base</small>
                        </div>
                      </div>

                      <div class="page-table-desc-column permission-modal__actions-column">
                        <span
                          class="material-icons-outlined save-icon"
                          :class="{ 'permission-modal__action--disabled': profile.is_base }"
                          @click.stop="save(profile)"
                        >
                          save
                        </span>
                        <span
                          v-show="!profile.loading"
                          class="material-icons-outlined remove-icon"
                          :class="{ 'permission-modal__action--disabled': profile.is_base }"
                          @click.stop="remove_profile(profile)"
                        >
                          delete
                        </span>
                        <cc-loader
                          v-show="profile.loading"
                          :show_txt="false"
                          class="loader float-right"
                        />
                      </div>
                    </div>
                  </div>

                  <div v-if="profiles.length === 0" class="permission-modal__empty-state">
                    Nenhum perfil disponível.
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section v-if="current_profile.id" class="permission-modal__panel">
            <div class="input-title">{{ current_profile.name }}</div>
            <div class="profiles-wrapper">
              <div class="page-table-header">
                <div class="page-table-header-text permission-modal__checkbox-column"></div>
                <div class="page-table-header-text permission-modal__module-column">Módulo</div>
              </div>

              <div
                v-for="module in current_modules"
                :key="'perm-res-' + module.resource"
                :class="{
                  selected: isSelectedModule(module),
                  'permission-modal__row--loading': isPermissionLoading(module),
                }"
              >
                <div class="page-table-row">
                  <div class="page-table-desc-column permission-modal__checkbox-column" @click.stop>
                    <span
                      v-if="isPermissionLoading(module)"
                      class="permission-modal__spinner"
                      aria-hidden="true"
                    />
                    <div v-else class="checkbox">
                      <cc-checkbox
                        :checked="has_permission(module)"
                        :disabled="current_profile.is_base"
                        @input="togglePermission(module, $event)"
                      />
                    </div>
                  </div>

                  <div
                    class="page-table-desc-column permission-modal__module-column"
                    @click="select_module(module)"
                  >
                    <div class="permission-modal__module-header">
                      <span>{{ module.name }}</span>
                      <small class="permission-modal__meta-pill">
                        {{ translateAction(module.action) }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="current_modules.length === 0" class="permission-modal__empty-state">
                Nenhuma permissão disponível.
              </div>
            </div>
          </section>

          <section v-if="current_profile.id" class="permission-modal__panel">
            <div class="input-title">
              {{ current_module.name || "Permissões" }}
            </div>
            <div class="profiles-wrapper">
              <div class="page-table-header permission-modal__check-all-header" @click="toggleCheckAll">
                <div class="page-table-header-text permission-modal__status-column">
                  <span v-if="checkAll" class="material-icons-outlined">check_circle</span>
                  <span v-else class="material-icons-outlined">radio_button_unchecked</span>
                </div>
                <div class="page-table-header-text permission-modal__permission-column">Permissão</div>
              </div>

              <div
                v-for="(permission, idx) in current_module.permissions"
                :key="'perm-' + permissionKey(permission)"
                :class="{
                  selected: has_permission(permission),
                  'permission-modal__row--loading': isPermissionLoading(permission),
                  'page-table-line-special': idx % 2 !== 0,
                  'permission-modal__readonly-row': current_profile.is_base,
                }"
                @click="!current_profile.is_base && togglePermission(permission)"
              >
                <div class="page-table-row">
                  <div class="page-table-desc-column permission-modal__status-column">
                    <span
                      v-if="isPermissionLoading(permission)"
                      class="permission-modal__spinner"
                      aria-hidden="true"
                    />
                    <span
                      v-else
                      class="material-icons-outlined success-update"
                      :class="{ visible: has_permission(permission) }"
                    >
                      check_circle
                    </span>
                  </div>

                  <div class="page-table-desc-column permission-modal__permission-column">
                    <div class="permission-modal__permission-header">
                      <p class="perm-name">
                        {{ permission.name || translateAction(permission.action) }}
                      </p>
                      <small class="permission-modal__meta-pill">
                        {{ translateAction(permission.action) }}
                      </small>
                    </div>
                    <small>{{ permission.description || translateAction(permission.action) }}</small>
                  </div>
                </div>
              </div>

              <div
                v-if="current_module.permissions && current_module.permissions.length === 0"
                class="permission-modal__empty-state"
              >
                Nenhuma permissão disponível para este módulo.
              </div>
            </div>
          </section>
        </template>
      </div>
    </div>

    <cc-loader-full v-if="loading2" />
  </div>
</template>

<script>
import ErrorHandlerService from "@/services/ErrorHandlerService";
import { loaderMixin } from "@/mixins/sweet-loader.mixin";
import ClientsProfilesService from "@/services/v3/clients/profiles.service";
import PermissionService from "@/services/v3/commons/permission.service";
import {
  buildPermissionLookup,
  buildPermissionModules,
  extractCollectionFromPayload,
  extractItemFromPayload,
  filterPermissionsByVisibility,
  getPermissionKey,
  normalizePermissionRecord,
  translatePermissionAction,
} from "@/helpers/permissions";

const EMPTY_MODULE = {
  name: "",
  resource: "",
  action: "",
  permissions: [],
};

export default {
  mixins: [loaderMixin],
  props: {
    id: { type: Number },
    closeDialog: { type: Function },
    reload: { type: Function },
  },
  data() {
    return {
      profiles: [],
      availablePermissions: [],
      current_profile_id: null,
      current_module_resource: null,
      new_profile: { name: "" },
      loading: false,
      loading2: false,
      permissionLoadingMap: {},
      svc: new ClientsProfilesService(),
      permissionSvc: new PermissionService(),
    };
  },
  computed: {
    current_profile() {
      return (
        this.profiles.find((profile) => String(profile.id) === String(this.current_profile_id)) || {
          permissions: [],
        }
      );
    },
    current_modules() {
      return buildPermissionModules(filterPermissionsByVisibility(this.availablePermissions, "client"));
    },
    current_module() {
      if (!this.current_modules.length) {
        return EMPTY_MODULE;
      }

      return (
        this.current_modules.find((module) => module.resource === this.current_module_resource) ||
        this.current_modules[0]
      );
    },
    selectedPermissionKeys() {
      return buildPermissionLookup(this.current_profile.permissions || []);
    },
    checkAll() {
      return (
        Array.isArray(this.current_module.permissions) &&
        this.current_module.permissions.length > 0 &&
        this.current_module.permissions.every((permission) => this.has_permission(permission))
      );
    },
  },
  methods: {
    permissionKey(permission) {
      return getPermissionKey(permission);
    },
    translateAction(action) {
      return translatePermissionAction(action);
    },
    normalizeProfile(profile = {}) {
      return {
        ...profile,
        permissions: Array.isArray(profile.permissions)
          ? profile.permissions.map((permission) => normalizePermissionRecord(permission))
          : [],
      };
    },
    replaceProfile(profile) {
      const normalizedProfile = this.normalizeProfile(profile);
      const profileIndex = this.profiles.findIndex(
        (item) => String(item.id) === String(normalizedProfile.id)
      );

      if (profileIndex === -1) {
        this.profiles.push(normalizedProfile);
      } else {
        this.$set(this.profiles, profileIndex, normalizedProfile);
      }

      this.current_profile_id = normalizedProfile.id;
    },
    updateCurrentProfilePermissions(permissions) {
      this.replaceProfile({
        ...this.current_profile,
        permissions,
      });
    },
    permissionLoadingKey(permission, profileId = this.current_profile.id) {
      return `${profileId || "none"}::${this.permissionKey(permission)}`;
    },
    setPermissionLoading(permission, isLoading) {
      const key = this.permissionLoadingKey(permission);

      if (isLoading) {
        this.$set(this.permissionLoadingMap, key, true);
        return;
      }

      this.$delete(this.permissionLoadingMap, key);
    },
    isPermissionLoading(permission) {
      return !!this.permissionLoadingMap[this.permissionLoadingKey(permission)];
    },
    ensureCurrentModuleSelection() {
      if (!this.current_modules.length) {
        this.current_module_resource = null;
        return;
      }

      const hasSelectedModule = this.current_modules.some(
        (module) => module.resource === this.current_module_resource
      );

      if (!hasSelectedModule) {
        this.current_module_resource = this.current_modules[0].resource;
      }
    },
    isSelectedProfile(profile) {
      return String(profile.id) === String(this.current_profile_id);
    },
    isSelectedModule(module) {
      return module.resource === this.current_module.resource;
    },
    has_permission(permission) {
      return this.selectedPermissionKeys.has(this.permissionKey(permission));
    },
    select_profile(profile) {
      this.current_profile_id = profile.id;
      this.ensureCurrentModuleSelection();
    },
    select_module(module) {
      this.current_module_resource = module.resource;
    },
    async loadPermissionCatalog() {
      const response = await this.permissionSvc.list();
      this.availablePermissions = extractCollectionFromPayload(response.data).map((permission) =>
        normalizePermissionRecord(permission)
      );
      this.ensureCurrentModuleSelection();
    },
    async loadProfiles(selectedProfileId = this.current_profile_id) {
      const response = await this.svc.list({ per_page: "all" });
      const profiles = extractCollectionFromPayload(response.data)
        .map((profile) => this.normalizeProfile(profile))
        .filter((profile) => !profile.is_base);

      this.profiles = profiles;

      if (
        selectedProfileId &&
        profiles.some((profile) => String(profile.id) === String(selectedProfileId))
      ) {
        this.current_profile_id = selectedProfileId;
      } else if (
        !profiles.some((profile) => String(profile.id) === String(this.current_profile_id))
      ) {
        this.current_profile_id = profiles[0] ? profiles[0].id : null;
      }

      this.ensureCurrentModuleSelection();
    },
    async load() {
      this.loading = true;

      try {
        await Promise.all([this.loadPermissionCatalog(), this.loadProfiles()]);
      } catch (error) {
        ErrorHandlerService.handle(error, this.$store);
      } finally {
        this.loading = false;
      }
    },
    async save(profile) {
      if (profile.is_base) {
        return;
      }

      this.present_loader("Salvando perfil...");

      try {
        const response = await this.svc.save(profile);
        const savedProfile = extractItemFromPayload(response.data);
        const selectedProfileId =
          (savedProfile && savedProfile.id) || profile.id || this.current_profile_id;

        await this.loadProfiles(selectedProfileId);
        this.new_profile = { name: "" };

        if (typeof this.reload === "function") {
          this.reload();
        }
      } catch (error) {
        ErrorHandlerService.handle(error, this.$store);
      } finally {
        this.dismiss_loader();
      }
    },
    async remove_profile(profile) {
      if (profile.is_base) {
        return;
      }

      this.$set(profile, "loading", true);

      try {
        await this.svc.remove(profile);
        await this.loadProfiles(this.isSelectedProfile(profile) ? null : this.current_profile_id);

        if (typeof this.reload === "function") {
          this.reload();
        }

        this.$store.dispatch("notification/add", {
          type: "success",
          message: "Removido com sucesso!",
        });
      } catch (error) {
        ErrorHandlerService.handle(error, this.$store);
      } finally {
        this.$set(profile, "loading", false);
      }
    },
    async updateProfilePermission(permission, enabled, options = {}) {
      if (!this.current_profile.id || this.current_profile.is_base || this.isPermissionLoading(permission)) {
        return;
      }

      const { silent = false, skipReload = false } = options;
      this.setPermissionLoading(permission, true);

      try {
        if (enabled) {
          const response = await this.svc.create_permission({
            profile_id: this.current_profile.id,
            resource: permission.resource,
            action: permission.action,
          });

          const createdPermission = normalizePermissionRecord(
            extractItemFromPayload(response.data) || {
              ...permission,
              profile_id: this.current_profile.id,
            }
          );

          this.updateCurrentProfilePermissions([
            ...this.current_profile.permissions.filter(
              (item) => this.permissionKey(item) !== this.permissionKey(createdPermission)
            ),
            createdPermission,
          ]);
        } else {
          const attachedPermission = this.current_profile.permissions.find(
            (item) => this.permissionKey(item) === this.permissionKey(permission)
          );

          if (!attachedPermission || !attachedPermission.id) {
            return;
          }

          await this.svc.remove_permission({
            profile_id: this.current_profile.id,
            id: attachedPermission.id,
          });

          this.updateCurrentProfilePermissions(
            this.current_profile.permissions.filter(
              (item) => this.permissionKey(item) !== this.permissionKey(permission)
            )
          );
        }

        if (!skipReload && typeof this.reload === "function") {
          this.reload();
        }

        if (!silent) {
          this.$store.dispatch("notification/add", {
            type: "success",
            message: enabled ? "Permissão salva com sucesso!" : "Removido com sucesso!",
          });
        }
      } catch (error) {
        ErrorHandlerService.handle(error, this.$store);
      } finally {
        this.setPermissionLoading(permission, false);
      }
    },
    togglePermission(permission, enabled) {
      if (this.isPermissionLoading(permission)) {
        return;
      }

      const targetState = typeof enabled === "boolean" ? enabled : !this.has_permission(permission);

      if (targetState === this.has_permission(permission)) {
        return;
      }

      return this.updateProfilePermission(permission, targetState);
    },
    async toggleCheckAll() {
      if (this.current_profile.is_base) {
        return;
      }

      const targetState = !this.checkAll;
      const targetPermissions = targetState
        ? this.current_module.permissions.filter((permission) => !this.has_permission(permission))
        : this.current_module.permissions.filter((permission) => this.has_permission(permission));

      if (!targetPermissions.length) {
        return;
      }

      const response = await this.$swal.fire({
        title: targetState
          ? "Deseja ativar todas as permissões?"
          : "Deseja desativar todas as permissões?",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: targetState
          ? "Sim, ativar permissões!"
          : "Sim, desativar permissões!",
      });

      if (!response.isConfirmed) {
        return;
      }

      this.loading2 = true;

      try {
        for (const permission of targetPermissions) {
          await this.updateProfilePermission(permission, targetState, {
            silent: true,
            skipReload: true,
          });
        }

        if (typeof this.reload === "function") {
          this.reload();
        }

        this.$store.dispatch("notification/add", {
          type: "success",
          message: targetState
            ? "Permissões salvas com sucesso!"
            : "Permissões removidas com sucesso!",
        });
      } finally {
        this.loading2 = false;
      }
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style lang="scss" scoped>
@import "./index.modal";

.modal-mask {
  position: fixed;
  z-index: 4;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 8px;
  box-sizing: border-box;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s ease;
  font-size: 1vw;
}

.modal-content-container {
  background-color: #ffffff;
  width: 100%;
  max-width: none;
  max-height: 92vh;
  overflow: hidden;
  border-radius: 12px;
}

.user-management__header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-radius: 12px 12px 0 0;
  background-color: var(--primary-color);
}

.user-management__header-title {
  color: #ffffff;
  font-weight: 500;
  font-size: 2.5em;
}

.closebtn-icon {
  color: #ffffff;
  font-size: 3.5em;
  cursor: pointer;
}

.permission-modal {
  padding: 24px;
  display: grid;
  grid-template-columns: minmax(320px, 420px) minmax(300px, 1fr) minmax(440px, 1.35fr);
  gap: 20px;
  height: 76vh;
  max-height: 820px;
}

.permission-modal__main-loader {
  margin: auto;
}

.permission-modal__panel {
  min-width: 0;
  min-height: 0;
}

.input-title {
  font-weight: 400;
  font-size: 1.6em;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #898989;
}

.input-container {
  width: 100%;
  border: 1px solid #e5e5e5;
  box-sizing: border-box;
  border-radius: 8px;
  padding: 12px;
  margin-top: 10px;
  display: flex;
}

.input-value {
  border: none;
  flex: 1;
  font-weight: 400;
  font-size: 1.5em;
  letter-spacing: 0.15px;
  color: #b3b3b3;
}

.input-value:focus {
  border: none;
  outline: none;
}

.input-icon {
  color: var(--primary-color);
  cursor: pointer;
}

.profiles-wrapper {
  margin-top: 16px;
  height: 58vh;
  min-height: 24rem;
  max-height: 36rem;
  border: 1px solid #ececec;
  border-radius: 12px;
  overflow: auto;
}

.page-table-content {
  min-width: 0;
}

.page-table-header {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fffefc;
  border-bottom: 1px solid #e5e5e5;
  font-weight: 400;
  color: #605f5f;
  display: flex;
  padding: 1em 0;
}

.page-table-header-text {
  padding-left: 10px;
  font-weight: 600;
  font-size: 1.19em;
  color: #505050;
}

.page-table-row {
  display: flex;
  align-items: center;
  font-weight: 300;
  font-size: 1.2em;
  color: #605f5f;
}

.page-table-desc-column {
  padding: 0.8em 0.7em;
}

.permission-modal__id-column {
  width: 22%;
}

.permission-modal__name-column {
  width: 53%;
}

.permission-modal__actions-column {
  width: 25%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.permission-modal__checkbox-column {
  width: 14%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.permission-modal__module-column {
  width: 86%;
  cursor: pointer;
}

.permission-modal__status-column {
  width: 14%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.permission-modal__permission-column {
  width: 86%;
  cursor: pointer;
}

.permission-modal__profile-row,
.permission-modal__id-trigger {
  cursor: pointer;
}

.permission-modal__id-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.permission-modal__id-icon {
  color: #a5adb8;
  font-size: 1em;
}

.permission-modal__profile-name {
  display: flex;
  align-items: center;
  gap: 8px;
}

.permission-modal__badge,
.permission-modal__meta-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  background: #eef3ff;
  color: #355ca8;
  font-size: 0.82em;
  font-weight: 700;
  white-space: nowrap;
}

.permission-modal__module-header,
.permission-modal__permission-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.permission-modal__action--disabled {
  opacity: 0.35;
  pointer-events: none;
}

.permission-modal__readonly-row {
  background: #f8fafc;
}

.permission-modal__row--loading {
  opacity: 0.82;
  background: linear-gradient(
    90deg,
    rgba(53, 92, 168, 0.04),
    rgba(53, 92, 168, 0.1),
    rgba(53, 92, 168, 0.04)
  );
  background-size: 200% 100%;
  animation: permission-modal-row-loading 1.1s ease-in-out infinite;
}

.permission-modal__spinner {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid #dbe4f5;
  border-top-color: #355ca8;
  border-radius: 50%;
  animation: permission-modal-spin 0.7s linear infinite;
}

.permission-modal__check-all-header {
  cursor: pointer;
}

.permission-modal__empty-state {
  color: #6e6e6e;
  text-align: center;
  padding: 20px;
  font-size: 1.1em;
}

.save-icon {
  color: var(--primary-color);
  cursor: pointer;
  font-size: 1.7em;
}

.remove-icon {
  color: #cd2a2a;
  font-size: 1.7em;
  cursor: pointer;
}

.success-update {
  visibility: hidden;
  color: #31a24c;
}

.success-update.visible {
  visibility: visible;
}

.perm-name {
  margin: 0;
}

.page-table-line-special {
  background: #f7f7f7;
}

.selected {
  background: #f5f8ff;
}

@keyframes permission-modal-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes permission-modal-row-loading {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

@media only screen and (max-width: 1100px) {
  .permission-modal {
    grid-template-columns: 1fr;
    overflow: auto;
  }

  .modal-mask {
    font-size: 1.8vw;
  }
}

@media only screen and (max-width: 800px) {
  .modal-mask {
    font-size: 2.4vw;
  }

  .permission-modal__id-column {
    width: 26%;
  }

  .permission-modal__name-column {
    width: 46%;
  }

  .permission-modal__actions-column {
    width: 28%;
  }
}

@media only screen and (max-width: 450px) {
  .modal-mask {
    font-size: 3vw;
  }

  .modal-content-container {
    width: 100%;
  }

  .permission-modal {
    padding: 16px;
  }
}
</style>
